import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiPlus, mdiDoorOpen, mdiTree, mdiCheckboxBlankOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { SectionHeader, EmptyOK, SourceChip, StatusBadge } from './primitives'
import { useSpaces, type SpacesKind, type ReliabilitySpace } from './useSpaces'
import { catalogForKind, type SpaceCatalogEntry } from './spacesCatalog'
import { EmptyState } from '../ui/empty-state'

/** Stable, ascii testid slug for a (possibly accented) space name. */
function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

type Props = {
  org: string
  buildingUid: string
  kind: SpacesKind
  title: string
  desc: string
  /** when provided, spaces are grouped under these levels (interior); else flat */
  levels?: string[]
}

export function SpacesSection({ org, buildingUid, kind, title, desc, levels }: Props) {
  const { t } = useTranslation('data-reliability')
  const { spaces, isLoading, setEquipmentState, confirmSpace, addEquipment, createSpaceFromSuggestion, canCreateSpace, canAddEquipment } =
    useSpaces(org, buildingUid, kind)
  const [selUid, setSelUid] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [addingEquip, setAddingEquip] = useState(false)
  const [newEquipName, setNewEquipName] = useState('')
  // Suggestions dismissed this session — UI-only, leaves no trace (spec #1071).
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())

  const pendingEquip = useMemo(
    () => spaces.flatMap((s) => s.equipment).filter((e) => e.state === 'ask').length,
    [spaces],
  )

  // Typical-space suggestions from the static catalog: proposed but not
  // persisted. Hide any whose name is already a real space (old buildings still
  // carrying the legacy socle assets) or was dismissed this session.
  const suggestions = useMemo(() => {
    const existing = new Set(spaces.map((s) => s.name))
    return catalogForKind(kind).filter((e) => !existing.has(e.name) && !dismissed.has(e.name))
  }, [spaces, kind, dismissed])

  const KindIcon = kind === 'exterior' ? mdiTree : mdiDoorOpen
  const testid = kind === 'exterior' ? 'dr-section-exterior' : 'dr-section-common'

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    try { await fn() } finally { setBusy(false) }
  }

  if (isLoading) {
    return (
      <div data-testid={testid}>
        <SectionHeader title={title} desc={desc} />
        <div className="h-32 animate-pulse rounded-xl border border-border bg-muted/30" />
      </div>
    )
  }
  if (spaces.length === 0 && suggestions.length === 0) {
    return (
      <div data-testid={testid}>
        <SectionHeader title={title} desc={desc} />
        <EmptyOK label={t('spaces.empty')} />
      </div>
    )
  }

  const groups = levels ?? ['__flat__']
  const selected = selUid ? spaces.find((s) => s.uid === selUid) : null

  const SuggestionCell = (e: SpaceCatalogEntry) => {
    const s = slug(e.name)
    return (
      <div
        key={`sugg-${e.name}`}
        className="flex min-w-[150px] items-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 p-3 text-left"
        data-testid={`dr-suggestion-${s}`}
      >
        <button
          type="button"
          onClick={() => run(() => createSpaceFromSuggestion(e))}
          disabled={busy || !canCreateSpace}
          className="flex flex-1 items-start gap-1.5 text-left"
          aria-label={t('spaces.confirm_suggestion', { name: e.name })}
          title={t('spaces.confirm_suggestion', { name: e.name })}
          data-testid={`dr-suggestion-confirm-${s}`}
        >
          <AppIcon tone="muted" path={mdiCheckboxBlankOutline} size="xs" className="mt-0.5 flex-shrink-0" />
          <span className="flex flex-col">
            <span className="text-sm font-semibold text-muted-foreground">{e.name}</span>
            <span className="text-[11px] text-muted-foreground/70">{t('spaces.suggestion_hint')}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => setDismissed((prev) => new Set(prev).add(e.name))}
          disabled={busy}
          className="flex-shrink-0 self-start text-muted-foreground/60 hover:text-foreground"
          aria-label={t('spaces.dismiss_suggestion', { name: e.name })}
          title={t('spaces.dismiss_suggestion', { name: e.name })}
          data-testid={`dr-suggestion-dismiss-${s}`}
        >
          <AppIcon path={mdiClose} size="xs" />
        </button>
      </div>
    )
  }

  const SpaceCell = (s: ReliabilitySpace) => {
    const confirmed = !s.pending
    const hasAsk = s.equipment.some((e) => e.state === 'ask')
    return (
      <button
        key={s.uid}
        onClick={() => {
          setSelUid(s.uid === selUid ? null : s.uid)
          setAddingEquip(false)
          setNewEquipName('')
        }}
        className={`min-w-[150px] rounded-xl border p-3 text-left transition-colors ${
          selUid === s.uid ? 'border-primary bg-primary/10' : confirmed ? 'border-success/40 bg-success/10' : 'border-border bg-muted/40 hover:border-primary/50'
        }`}
        data-testid={`dr-space-${s.uid}`}
      >
        <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          {confirmed && <AppIcon path={mdiCheck} size="xs" className="text-success" />}
          {s.name}
        </div>
        <div className={`mt-1 text-[11px] ${hasAsk ? 'text-primary' : 'text-muted-foreground'}`}>
          {s.equipment.length ? t('spaces.equipment_count', { count: s.equipment.length }) : t('spaces.no_equipment')}
        </div>
      </button>
    )
  }

  return (
    <div data-testid={testid}>
      <SectionHeader title={title} desc={desc} />

      {/* equipment-pending strip */}
      <div className={`mb-3.5 flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm ${pendingEquip ? 'text-primary' : 'text-success'}`}>
        <AppIcon path={pendingEquip ? KindIcon : mdiCheck} size="sm" />
        {pendingEquip ? t('spaces.equipment_to_confirm', { count: pendingEquip }) : t('spaces.all_confirmed')}
      </div>

      {/* space cells */}
      <div className="rounded-xl border border-border bg-card px-3.5 pb-4 pt-2">
        {groups.map((lv) => {
          const items = levels ? spaces.filter((s) => s.level === lv) : spaces
          const suggItems = levels ? suggestions.filter((e) => e.level === lv) : suggestions
          if (!items.length && !suggItems.length) return null
          return (
            <div key={lv} className={`flex items-start gap-3.5 py-3 ${levels ? 'border-b border-border/50 last:border-b-0' : ''}`}>
              {levels && <div className="w-28 flex-shrink-0 pt-2 text-xs font-bold text-foreground">{lv}</div>}
              <div className="flex flex-wrap gap-2.5">
                {items.map(SpaceCell)}
                {suggItems.map(SuggestionCell)}
              </div>
            </div>
          )
        })}
      </div>

      {/* space detail */}
      {selected && (
        <div className="mt-3.5 rounded-xl border border-primary bg-card p-4" data-testid="dr-space-detail">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-base font-bold text-foreground">{selected.name}</span>
              {selected.level && <span className="text-xs text-muted-foreground">{selected.level}</span>}
              <SourceChip org={org} source={selected.source} />
            </div>
            <button onClick={() => setSelUid(null)} className="text-muted-foreground" aria-label="close" data-testid="dr-space-detail-close"><AppIcon path={mdiClose} size="sm" /></button>
          </div>

          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('spaces.located_equipment')}</div>
          {selected.equipment.length === 0 && (
            <EmptyState variant="inline" className="mb-3 py-3" title={t('spaces.no_equipment_extracted')} />
          )}
          {selected.equipment.map((e, i) => {
            const ok = e.state === 'confirmed' || e.state === 'found'
            const absent = e.state === 'absent'
            return (
              <div key={e.uid} className={`flex items-center gap-3 py-2.5 ${i ? 'border-t border-border/50' : ''} ${absent ? 'opacity-50' : ''}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{e.name}</span>
                    {e.state === 'found' && <StatusBadge tone="primary">{t('spaces.found_in_docs')}</StatusBadge>}
                    {e.state === 'ask' && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('spaces.to_confirm')}</span>}
                  </div>
                  {e.source && <div className="mt-1.5"><SourceChip org={org} source={e.source} /></div>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => run(() => setEquipmentState(e, 'confirmed'))}
                    disabled={busy}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${ok ? 'border-success bg-success/15 text-success' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    data-testid={`dr-equip-present-${e.uid}`}
                  >
                    <AppIcon path={mdiCheck} size="xs" /> {t('spaces.present')}
                  </button>
                  <button
                    onClick={() => run(() => setEquipmentState(e, 'absent'))}
                    disabled={busy}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${absent ? 'border-muted-foreground bg-muted text-muted-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
                    data-testid={`dr-equip-absent-${e.uid}`}
                  >
                    <AppIcon path={mdiClose} size="xs" /> {t('spaces.absent')}
                  </button>
                </div>
              </div>
            )
          })}

          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => run(() => confirmSpace(selected))} disabled={busy || !selected.pending} data-testid="dr-space-confirm">{t('spaces.confirm_space')}</Button>
            {addingEquip ? (
              <>
                <Input
                  autoFocus
                  value={newEquipName}
                  onChange={(e) => setNewEquipName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newEquipName.trim()) {
                      run(async () => {
                        await addEquipment(selected, newEquipName)
                        setNewEquipName('')
                        setAddingEquip(false)
                      })
                    } else if (e.key === 'Escape') {
                      setAddingEquip(false)
                      setNewEquipName('')
                    }
                  }}
                  placeholder={t('spaces.add_equipment_placeholder')}
                  className="h-8 w-48 text-xs"
                  data-testid="dr-add-equipment-input"
                />
                <Button
                  size="sm"
                  onClick={() => run(async () => {
                    await addEquipment(selected, newEquipName)
                    setNewEquipName('')
                    setAddingEquip(false)
                  })}
                  disabled={busy || !newEquipName.trim()}
                  data-testid="dr-add-equipment-confirm"
                >
                  <AppIcon path={mdiCheck} size="xs" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setAddingEquip(false); setNewEquipName('') }}
                  disabled={busy}
                  aria-label={t('spaces.cancel')}
                  data-testid="dr-add-equipment-cancel"
                >
                  <AppIcon path={mdiClose} size="xs" />
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" disabled={busy || !canAddEquipment} onClick={() => setAddingEquip(true)} data-testid="dr-add-equipment-btn">
                <AppIcon path={mdiPlus} size="sm" className="mr-1" />{t('spaces.add_equipment')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
